import React, { Component } from 'react';
import Form from 'react-bootstrap/Form';

import { DESCRIPTION_ROWS, TITLE_ROWS } from '../../components/autoGrow';
import GrowTextarea from '../../components/growTextarea';
import CellDetailsView from './cellDetailsView';
import { isStructureEditable } from './cellRules';
import { upsertCell } from '../../domain/model';

/**
 * CellDetailsSection — a cell's title and description, editable or not, and the rule for which.
 *
 * The two surfaces that show a cell — the mission map's dock and the cell's own screen — are one
 * click apart and read as one product. The dock could edit these while the cell was a draft and the
 * screen never could, so the same cell in the same state offered different things depending on how
 * you had arrived at it.
 *
 * The RULE lives here rather than in either of them, which is the point: a caller says what it
 * knows (`canEdit`, the revision and session gate) and this decides the rest. A caller that had to
 * remember `status === 'draft' && canEdit` is a caller that can forget it, and one of them did.
 *
 * Editing stops at draft exit because the orchestrator is walking the cell from then on — its brief
 * has already been dispatched, and rewriting it would describe work that is not the work being
 * done. Past that it renders CellDetailsView, the same read-only pair the dock has always shown.
 *
 * Saves on BLUR, not per keystroke: a title is written a word at a time and a write per keystroke
 * is a write per keystroke. The draft is re-seeded when the cell IDENTITY changes — not on every
 * prop change, which would discard what somebody was typing each time the heartbeat touched the
 * cell.
 *
 * Props:
 *   cell, missionId, client   — what to write and where
 *   onApplyLocal              — (updater) => void; reflect the write without a reload
 *   canEdit                   — false on a committed revision, a closed session, or no write access
 */
class CellDetailsSection extends Component {
  constructor(props) {
    super(props);
    const cellProps = (props.cell && props.cell.props) || {};
    this.state = {
      titleDraft: cellProps.title || '',
      descriptionDraft: cellProps.description || '',
    };
  }

  componentDidUpdate(previous) {
    if ((previous.cell && previous.cell.element_id) !== (this.props.cell && this.props.cell.element_id)) {
      const cellProps = (this.props.cell && this.props.cell.props) || {};
      this.setState({
        titleDraft: cellProps.title || '',
        descriptionDraft: cellProps.description || '',
      });
    }
  }

  async _save(field, next) {
    const { cell, missionId, client, onApplyLocal } = this.props;
    if (!cell || !client) return;
    if (next === ((cell.props || {})[field] || '')) return;
    try {
      await client.setCellProps(missionId, cell.element_id, { [field]: next });
      if (onApplyLocal) {
        onApplyLocal(s => upsertCell(s, missionId, {
          ...cell, props: { ...(cell.props || {}), [field]: next },
        }));
      }
    } catch (_) {
      // Ignore; a later reload will reflect the server state.
    }
  }

  render() {
    const { cell, canEdit, collapsed } = this.props;
    if (!isStructureEditable(cell) || canEdit === false) {
      return <CellDetailsView cell={cell} collapsed={collapsed} />;
    }
    const { titleDraft, descriptionDraft } = this.state;
    return (
      <>
        {/* TITLE and DESCRIPTION are what these fields are called on every surface that touches
            them — these panels, both creation modals, the orchestrator and the seeds — so the words
            a person reads here are the words the rest of the system uses. */}
        <Form.Group>
          <GrowTextarea
            rows={1}
            maxRows={TITLE_ROWS}
            value={titleDraft}
            placeholder="Cell title…"
            onChange={value => this.setState({ titleDraft: value })}
            onBlur={() => this._save('title', titleDraft)}
          />
          {!collapsed && <Form.Text muted>What this cell is called.</Form.Text>}
        </Form.Group>
        {/* Collapsing Details keeps the TITLE: it is the cell's name, and a section that hid it
            would leave a header called "Details" standing in for one. The description is the part
            that costs the screen, so it is the part that goes. The draft above is untouched — this
            section stays mounted while it is collapsed, so half-typed prose survives a fold. */}
        {!collapsed && (
        <Form.Group>
          <GrowTextarea
            rows={2}
            maxRows={DESCRIPTION_ROWS}
            value={descriptionDraft}
            placeholder="Cell description…"
            onChange={value => this.setState({ descriptionDraft: value })}
            onBlur={() => this._save('description', descriptionDraft)}
          />
          <Form.Text muted>What this cell is about.</Form.Text>
        </Form.Group>
        )}
      </>
    );
  }
}

export default CellDetailsSection;
